import { formatDateYMD } from "../../../shared/utils/date";
import { csvEscape } from "../../company/utils/csvAndDateUtils";
import type { SeatHistoryEntry } from "./types";
import { downloadCsvFile } from "./utils";

interface ExportColumn {
  header: string;
  accessor: (entry: SeatHistoryEntry) => string;
}

/**
 * Export seat history data to CSV and trigger a download.
 *
 * @param data - The seat entries to export (respects active filters)
 * @param columnHeaders - Column header labels matching the displayed table
 * @param filename - The name for the downloaded file
 */
export function exportSeatHistoryCsv(
  data: SeatHistoryEntry[],
  columnHeaders: {
    grantedViaLicense: string;
    anonymizedUser: string;
    dateAssigned: string;
    currentExpiryDate: string;
    originalExpiryDate: string;
  },
  filename: string,
): void {
  const columns: ExportColumn[] = [
    {
      header: columnHeaders.grantedViaLicense,
      accessor: (e) => e.grantedViaLicense,
    },
    {
      header: columnHeaders.anonymizedUser,
      accessor: (e) => e.anonymizedUser,
    },
    {
      header: columnHeaders.dateAssigned,
      accessor: (e) => formatDateYMD(e.dateAssigned),
    },
    {
      header: columnHeaders.currentExpiryDate,
      accessor: (e) => formatDateYMD(e.currentExpiryDate),
    },
    {
      header: columnHeaders.originalExpiryDate,
      accessor: (e) => formatDateYMD(e.originalExpiryDate),
    },
  ];

  const headerRow = columns.map((c) => csvEscape(c.header)).join(",");
  const rows = data.map((entry) =>
    columns.map((col) => csvEscape(col.accessor(entry))).join(","),
  );

  const csvContent = [headerRow, ...rows].join("\r\n");
  downloadCsvFile(csvContent, filename);
}

/** Generate a default filename for the seat history export */
export function makeSeatHistoryExportFilename(
  startDate: Date,
  endDate: Date,
): string {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  return `seat-history_${fmt(startDate)}_${fmt(endDate)}.csv`;
}
